import { Box, useTheme, Grid, Button } from '@mui/material';
import { useState, useCallback } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { TarotCard } from '@/context/CardsContext';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2 } from 'lucide-react';
import CardSelection from './CardSelection';
import { useNavigate } from 'react-router-dom';
import { useDailyReading } from '@/context/DailyReadingContext';
import DailyReadingEmptyState from './DailyReadingEmptyState';
import DailyReadingCardDisplay from './DailyReadingCardDisplay';
import { track } from '@/third-party/MixPanel';


const OneCardFastReading = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const {
        selectedCard,
        isCardFlipped,
        readingId,
        isCreatingReading,
        readingComplete,
        selectCard,
        resetReading
    } = useDailyReading();

    // UI state only
    const [selectionDrawerOpen, setSelectionDrawerOpen] = useState(false);
    const [selectionKey, setSelectionKey] = useState(0);

    const openSelectionDrawer = useCallback(() => {
        track('daily_reading_card_selection_opened');
        resetReading();
        setSelectionDrawerOpen(true);
    }, [resetReading]);

    const closeSelectionDrawer = useCallback(() => {
        track('daily_reading_card_selection_closed', {
            cardSelected: !!selectedCard
        });
        setSelectionDrawerOpen(false);
        // Increment key to force remount and reset selection
        setSelectionKey(prev => prev + 1);
    }, [selectedCard]);

    const handleSelectionComplete = useCallback(async (selectedCards: TarotCard[]) => {
        console.log('Selected cards:', selectedCards);

        if (selectedCards.length > 0) {
            const card = selectedCards[0];
            track('daily_reading_card_confirmed', {
                cardId: card.id,
                reversed: card.reversed
            });
            await selectCard(card);
        }

        // Close drawer after receiving selection
        closeSelectionDrawer();
    }, [selectCard, closeSelectionDrawer]);

    return (
        <Box sx={{ width: '100%' }} className="text-optimized">

            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                        <Typography variant="h6" sx={{
                            color: theme.palette.background.dark,
                            fontFamily: '"Playfair Display", serif',
                            fontWeight: 600
                        }}>
                            {t('home.dailyReading')}
                        </Typography>

                    </Box>
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>

                    {selectedCard && <Button
                        size="small"
                        variant="contained"
                        disabled={!readingComplete || !readingId}
                        onClick={() => {
                            track('daily_reading_complete_button_clicked', {
                                readingId
                            });
                            readingId && navigate(`/reading/${readingId}`);
                        }}
                        sx={{
                            fontFamily: '"Playfair Display", serif',
                            fontSize: '0.7rem',
                            py: 0.5,
                            px: 1,
                            marginRight: '10px',
                        }}
                    >
                        {isCreatingReading ? (
                            <Loader2
                                className="animate-spin"
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    marginBottom: '2px',
                                    marginRight: '5px',
                                }}
                            />
                        ) : (
                            <Sparkles
                                className={readingComplete ? "animate-pulse" : ""}
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    marginBottom: '2px',
                                    marginRight: '5px',
                                }}
                            />
                        )}
                        {isCreatingReading ? t('home.preparingReading') || 'Preparing...' : t('home.completeReading')}
                    </Button>}
                </Grid>
            </Grid>
            {!selectedCard && <DailyReadingEmptyState onChooseCard={openSelectionDrawer} />}
            {selectedCard && <DailyReadingCardDisplay card={selectedCard} isCardFlipped={isCardFlipped} />}


            <SwipeableDrawer
                anchor="bottom"
                open={selectionDrawerOpen}
                onClose={closeSelectionDrawer}
                onOpen={() => setSelectionDrawerOpen(true)}
                slotProps={{
                    paper: {
                        sx: {
                            height: '100%',

                            padding: '20px',

                        }
                    }
                }}
            >
                <CardSelection
                    maxSelections={1}
                    key={selectionKey}
                    onSelectionComplete={handleSelectionComplete}
                />
            </SwipeableDrawer>



        </Box>
    );
};

export default OneCardFastReading;
